import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { LoaderConfig, RhinoImportOptions } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useEditorStore } from "../../../../state/editor/useEditor";
import { captureHost } from "../../scene/sceneFile";
import { createEmbed, getStorage } from "../../../../features/vault/vaultApi";
import { uploadAsset, type AssetKind } from "../../../../features/assets/assetsApi";
import { useCaps } from "../../CapsProvider";
import {
  CompactCheck,
  CompactColor,
  CompactNumber,
  CompactSlider,
  CompactText,
  MiniBtn,
  Row,
} from "../../inspector/rows";
import type { InertTag } from "../../inspector/schema";
import { pickFile } from "../../../../util/download";
import { confirmDialog, promptDialog } from "@/shared/ui";

/**
 * Plugins-tab bespoke controls (Wave I). Each round-trips through the engine RPC
 * where one exists (loader / frame-fade / S3 / Rhino import options), else holds
 * faithful local state for stored-only / best-effort plugins (material-extension
 * master toggles, model-stage). Labels / defaults are transcribed verbatim from
 * the captured iJewel field schema.
 */

// ─── Loading Screen (LoadingScreenPlugin) ──────────────────────────────────
export function LoadingScreenControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [c, setC] = useState<LoaderConfig | null>(null);
  useEffect(() => { r.getLoaderConfig().then(setC).catch(() => {}); }, [r]);
  if (!c) return null;
  const set = (patch: Partial<LoaderConfig>) => { setC({ ...c, ...patch }); void r.setLoaderConfig({ ...c, ...patch }); };
  const chk = (label: string, key: keyof LoaderConfig, def = false) => (
    <Row label={label}><CompactCheck checked={(c[key] as boolean | undefined) ?? def} onChange={(v) => set({ [key]: v } as Partial<LoaderConfig>)} /></Row>
  );
  return (
    <>
      {chk("Enabled", "enabled", true)}
      <Row label="Loading text header"><CompactText value={c.loadingTextHeader ?? ""} onChange={(v) => set({ loadingTextHeader: v })} /></Row>
      <Row label="Error text header"><CompactText value={c.errorTextHeader ?? ""} onChange={(v) => set({ errorTextHeader: v })} /></Row>
      {chk("Show file names", "showFileNames", true)}
      {chk("Show process states", "showProcessStates", true)}
      {chk("Show progress", "showProgress", true)}
      {chk("Hide on only errors", "hideOnOnlyErrors", true)}
      {chk("Hide on files load", "hideOnFilesLoad", true)}
      {chk("Hide on scene object load", "hideOnSceneObjectLoad", false)}
      {chk("Minimize on scene object load", "minimizeOnSceneObjectLoad", true)}
      {chk("Show when files start loading", "showOnFilesLoading", true)}
      {chk("Show when scene empty", "showOnSceneEmpty", true)}
      <Row label="Hide delay (ms)"><CompactNumber value={c.hideDelay ?? 500} min={0} step={50} width={76} onChange={(v) => set({ hideDelay: v })} /></Row>
      <Row label="Background Opacity"><CompactSlider value={c.backgroundOpacity ?? 0.5} min={0} max={1} step={0.01} onChange={(v) => set({ backgroundOpacity: v })} /></Row>
      <Row label="Background Blur"><CompactSlider value={c.backgroundBlur ?? 24} min={0} max={100} step={1} onChange={(v) => set({ backgroundBlur: v })} /></Row>
      <Row label="Background Color"><CompactColor value={c.background ?? "#ffffff"} onChange={(v) => set({ background: v })} /></Row>
      <Row label="Text Color"><CompactColor value={c.textColor ?? "#222222"} onChange={(v) => set({ textColor: v })} /></Row>
      <Row label="Logo Image"><CompactText value={c.logoImage ?? ""} placeholder="URL / data-URI" onChange={(v) => set({ logoImage: v })} /></Row>
      <div className="flex gap-1.5 px-3 py-1.5">
        <MiniBtn icon="visibility" onClick={() => void r.showLoader()}>Preview</MiniBtn>
        <MiniBtn icon="visibility_off" onClick={() => void r.hideLoader()}>Hide</MiniBtn>
      </div>
    </>
  );
}

// ─── Full Screen (FullScreenPlugin) ────────────────────────────────────────
export function FullScreenControl() {
  const { client } = useViewer();
  const r = client.ring;
  return (
    <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
      <MiniBtn icon="fullscreen" primary onClick={() => void r.enterFullScreen()}>Enter FullScreen</MiniBtn>
      <MiniBtn icon="fullscreen_exit" onClick={() => void r.exitFullScreen()}>Exit FullScreen</MiniBtn>
      <MiniBtn icon="aspect_ratio" onClick={() => void r.toggleFullScreen()}>Toggle FullScreen</MiniBtn>
    </div>
  );
}

// ─── Frame Fade (FrameFade) ────────────────────────────────────────────────
export function FrameFadeControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [enabled, setEnabled] = useState(true);
  useEffect(() => { r.getFrameFade().then((f) => setEnabled(f.enabled)).catch(() => {}); }, [r]);
  return (
    <Row label="Enabled">
      <CompactCheck checked={enabled} onChange={(v) => { setEnabled(v); void r.setFrameFade(v); }} />
    </Row>
  );
}

// ─── Model Stage (ModelStagePlugin) ────────────────────────────────────────
export function ModelStageControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const [groundBelow, setGroundBelow] = useState(false);
  // One shared boolean used to render "Loading…" on the *sample* button only, so
  // a multi-MB import started from "Prompt Local File" / "Prompt URL" showed no
  // feedback anywhere. Track WHICH action is in flight instead.
  const [busy, setBusy] = useState<null | "file" | "url" | "sample">(null);
  const [err, setErr] = useState<string | null>(null);
  const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

  const addFromUrl = async (which: "file" | "url" | "sample", url: string) => {
    setErr(null);
    setBusy(which);
    // A 404 / unsupported format rejects here; the viewer bypasses its own loader
    // overlay for stage props (Viewer.addStageModel), so without this catch the
    // user is left staring at an unchanged scene believing the import worked.
    try { await r.addStageModel(url); }
    catch (e) { setErr(`Stage model failed: ${msg(e)}`); }
    finally { setBusy(null); }
  };
  // addStageModel only accepts a URL, and a blob: URL created in this (playground)
  // origin cannot be fetched from the viewer iframe's (different-origin) document —
  // inline it as a data: URL instead so no cross-origin fetch is needed.
  const addFromUrlFallback = (f: File) =>
    new Promise<void>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== "string") { setErr("Could not read that file."); resolve(); return; }
        // Reported as "file": the click came from "Prompt Local File", so that is
        // the button that must carry the pending state.
        void addFromUrl("file", reader.result).then(resolve);
      };
      // FileReader had no onerror at all, so an unreadable file was a permanent
      // silent no-op that also never resolved the caller's busy state.
      reader.onerror = () => { setErr(`Could not read ${f.name}.`); resolve(); };
      reader.readAsDataURL(f);
    });
  // v31: addStageModelFile transfers the File directly over the RPC bridge
  // (mirrors addModelFile) — no more data-URL round trip needed.
  const addFromFile = async (f: File) => {
    if (!caps.v31) { await addFromUrlFallback(f); return; }
    setErr(null);
    setBusy("file");
    try { await r.addStageModelFile(f); }
    catch (e) { setErr(`Stage model failed: ${msg(e)}`); }
    finally { setBusy(null); }
  };
  const promptUrl = async () => {
    const u = await promptDialog({ title: "Stage model URL", label: "URL" });
    if (u == null) return;
    await addFromUrl("url", u);
  };
  const toggleGroundBelow = (v: boolean) => {
    setGroundBelow(v);
    setErr(null);
    const p = caps.v31 ? r.setStageGroundBelow(v) : r.setGroundShadowBake({ enabled: true });
    // Optimistic checkbox — roll it back rather than leave the UI claiming a
    // setting the viewer never took.
    void p.catch((e) => { setGroundBelow(!v); setErr(`Ground below model failed: ${msg(e)}`); });
  };
  return (
    <>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="folder_open"
          busy={busy === "file"}
          disabled={busy !== null && busy !== "file"}
          onClick={() => { void pickFile(".glb,.gltf,.obj,.fbx,.stl,.3dm", addFromFile); }}
        >
          {busy === "file" ? "Loading…" : "Prompt Local File"}
        </MiniBtn>
        <MiniBtn icon="link" busy={busy === "url"} disabled={busy !== null && busy !== "url"} onClick={() => void promptUrl()}>
          {busy === "url" ? "Loading…" : "Prompt URL"}
        </MiniBtn>
      </div>
      <Row label="Ground below model"><CompactCheck checked={groundBelow} onChange={toggleGroundBelow} /></Row>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="view_in_ar"
          busy={busy === "sample"}
          disabled={busy !== null && busy !== "sample"}
          onClick={() => void addFromUrl("sample", "/models/demo.glb")}
        >
          {busy === "sample" ? "Loading…" : "Load sample model"}
        </MiniBtn>
      </div>
      {err ? <div className="px-3 pb-1 text-[11px] text-ed-red">{err}</div> : null}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">
        Stage props compose a product shot; they are excluded from auto-fit and export.
      </div>
    </>
  );
}

// ─── Rhino 3dm loader (Rhino3dmLoadPlugin) ─────────────────────────────────
/** three's 3DMLoader takes no per-import options, so the viewer emulates them in
 *  a post-load pass (AssetLoader._applyRhinoOptions) — and that pass implements
 *  only three of the six the plugin declares. */
const RHINO_UNREAD_INERT: InertTag = {
  reason:
    "Stored, never read. AssetLoader._applyRhinoOptions honours only Import materials, Hide line mesh and Hide point mesh; layer materials, instancing and user-data strings have no post-load equivalent and three's 3DMLoader exposes no hook for them.",
};

export function RhinoLoaderControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [o, setO] = useState<RhinoImportOptions | null>(null);
  useEffect(() => { r.getRhinoImportOptions().then(setO).catch(() => {}); }, [r]);
  if (!o) return null;
  const set = (patch: Partial<RhinoImportOptions>) => r.setRhinoImportOptions(patch).then(setO).catch(() => {});
  const chk = (label: string, key: keyof RhinoImportOptions, inert?: InertTag) => (
    <Row label={label} inert={inert}><CompactCheck checked={o[key]} onChange={(v) => void set({ [key]: v } as Partial<RhinoImportOptions>)} /></Row>
  );
  return (
    <>
      {chk("Import materials", "importMaterials")}
      {chk("Force layer materials", "forceLayerMaterials", RHINO_UNREAD_INERT)}
      {chk("Replace with instanced mesh", "replaceWithInstancedMesh", RHINO_UNREAD_INERT)}
      {chk("Hide line mesh", "hideLineMesh")}
      {chk("Hide point mesh", "hidePointMesh")}
      {chk("Load user data strings", "loadUserDataStrings", RHINO_UNREAD_INERT)}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">Applied on the next .3dm import.</div>
    </>
  );
}

// ─── S3 Connection (AWSClientPlugin) ───────────────────────────────────────
/**
 * Export to storage (the checklist calls this section "S3 Connection").
 *
 * It used to collect an AWS **Access Key Secret in an unmasked text box** and
 * fire `setS3Config` on every keystroke — pushing the secret over postMessage
 * into the viewer iframe, a different origin. It never reached our server, was
 * never encrypted, and uploaded nothing: `Viewer.s3Connect` toggles a boolean.
 *
 * All of that was a second, worse copy of a feature the Vault already has.
 * Settings → Storage validates keys through STS, provisions the bucket,
 * encrypts the secret at rest and never returns it — and `POST /api/v1/assets`
 * already calls `getWorkspaceS3Config`, so an upload lands in the customer's
 * OWN bucket whenever they have connected one. Wiring here gives BYO-S3 for
 * free, through a path that is provisioned, permissioned and audited.
 *
 * `S3Config` / `s3Connect` stay on the contract as a deprecated stub: removing
 * them is a cross-repo breaking change for no gain.
 */
export function S3Control() {
  const { client } = useViewer();
  const r = client.ring;
  const [storage, setStorage] = useState<{ enabled: boolean; bucket: string | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    void getStorage()
      .then((st) => setStorage({ enabled: st.enabled, bucket: st.bucket ?? null }))
      // Unauthenticated (/dev/studio) or the API is down. "We don't know" is
      // the honest render, not an error banner for something nobody invoked.
      .catch(() => setStorage(null));
  }, []);

  /** 40 MB body limit, and base64 inflates 4/3 — so this is the real ceiling. */
  const MAX_BYTES = 28 * 1024 * 1024;

  const upload = async (id: string, name: string, kind: AssetKind, make: () => Promise<Blob>) => {
    if (busy) return;
    setBusy(id);
    setErr(null);
    setUrl(null);
    try {
      const blob = await make();
      if (blob.size > MAX_BYTES) {
        throw new Error(
          `That export is ${(blob.size / 1048576).toFixed(1)} MB; the upload limit is 28 MB. Render it smaller and try again.`,
        );
      }
      const dataUrl = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result));
        fr.onerror = () => rej(new Error("Could not read the export."));
        fr.readAsDataURL(blob);
      });
      const asset = await uploadAsset({ name, kind, dataUrl });
      setUrl(asset.url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(null);
    }
  };

  const stamp = () => new Date().toISOString().replace(/[:.]/g, "-");

  return (
    <>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        <MiniBtn
          icon="image"
          primary
          busy={busy === "png"}
          disabled={busy !== null && busy !== "png"}
          onClick={() =>
            void upload("png", `studio-${stamp()}.png`, "image", async () => {
              const p = await r.getRendererParams().catch(() => null);
              const custom = p?.mode === "custom" && p.width > 0 && p.height > 0;
              return r.snapshot({
                width: custom ? p.width : (p?.width || 1600),
                height: custom ? p.height : (p?.height || 1600),
              });
            })
          }
        >
          Upload snapshot
        </MiniBtn>
        <MiniBtn
          icon="deployed_code"
          busy={busy === "glb"}
          disabled={busy !== null && busy !== "glb"}
          onClick={() =>
            void upload("glb", `studio-${stamp()}.glb`, "glb", () => r.exportGLB({ compress: true }))
          }
        >
          Upload model
        </MiniBtn>
      </div>

      {url ? (
        <div className="flex flex-col gap-1 px-3 pb-2">
          <span className="text-[12px] font-semibold text-ed-text-2">Uploaded</span>
          <CompactText value={url} onChange={() => {}} />
        </div>
      ) : null}
      {err ? <div className="px-3 pb-1 text-[11px] leading-snug text-ed-red">{err}</div> : null}

      <div className="px-3 pb-2 text-[11px] leading-snug text-ed-text-3">
        {storage?.enabled
          ? `Uploads go to your own bucket${storage.bucket ? ` (${storage.bucket})` : ""}, served from your CDN.`
          : "Uploads go to your workspace storage. Connect your own AWS bucket under Settings → Storage and they will land there instead — this editor never handles your keys."}
      </div>
    </>
  );
}

// ─── Share Link (TransfrSharePlugin) ───────────────────────────────────────
/**
 * Share Link — a real, hosted link.
 *
 * This used to base64 the scene inline and truncate it to 4000 characters into
 * a `#scene=` fragment that NOTHING in the app ever read, under a footnote
 * saying there was no share-hosting server. There is one: server/modules/embeds
 * serves a public GET /embeds/:token/resolve and the SPA routes /embed/:token.
 *
 * Both buttons mint ONE embed and hand out two views of it:
 *   viewer  → /embed/<token>          public, anyone with the link
 *   editor  → /studio/<id>?embed=…    workspace members, opens in the studio
 *
 * The current scene rides on the embed's `options.vjson`, which resolveEmbed
 * prefers over the model's last save — so the link shows what was on screen,
 * not whatever was saved last.
 */
/** A file from the user's computer up to this size rides inside a shared link's scene (same as a Vault save). */
const SHARE_INLINE_BUDGET = 4 * 1024 * 1024;

export function ShareLinkControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const store = useEditorStore();
  const { modelId } = useParams<{ modelId: string }>();
  const [link, setLink] = useState<string>("");
  const [busy, setBusy] = useState<null | "edit" | "view">(null);
  const [err, setErr] = useState<string | null>(null);

  // No model = nothing to hang an embed on (/dev/studio, or a scene loaded from
  // disk). Say so on the buttons rather than emitting a link that cannot work.
  const blocked = modelId ? null : "Open a model from the Vault to share a link";

  const make = async (which: "edit" | "view") => {
    if (!modelId) return;
    setErr(null);
    setBusy(which);
    try {
      // v65: the whole scene, saved the way the Vault saves one (files by
      // address, small local ones inline). The configurator stays the
      // model's own (config.configurator), as on the Vault save.
      const vjson = caps.sceneFileV65
        ? await r.exportSceneJSON({
            assets: "reference",
            inlineBudgetBytes: SHARE_INLINE_BUDGET,
            host: captureHost(client, store, { configurator: false }),
          })
        : await r.exportVjson({ pretty: false });
      const { embed, embedUrl } = await createEmbed(modelId, [], { vjson });
      setLink(which === "view" ? embedUrl : `${window.location.origin}/studio/${modelId}?embed=${embed.token}`);
    } catch (e) {
      // Was `catch { setLink("") }` — a failed export blanked the field and said
      // nothing, so it read as "nothing happened" rather than "it failed". A 403
      // here is real and specific: sharing needs the share_models permission.
      setLink("");
      setErr(`Could not create a share link: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        {/* MiniBtn takes no `title`, so the hover reason lives on a wrapper. */}
        <span title={blocked ?? "Opens in the studio — workspace members only"}>
          <MiniBtn icon="edit" busy={busy === "edit"} disabled={!!blocked || (busy !== null && busy !== "edit")}
            onClick={() => void make("edit")}>Share editor link</MiniBtn>
        </span>
        <span title={blocked ?? "Public link — anyone can open it"}>
          <MiniBtn icon="visibility" busy={busy === "view"} disabled={!!blocked || (busy !== null && busy !== "view")}
            onClick={() => void make("view")}>Share viewer link</MiniBtn>
        </span>
      </div>
      {err ? <div className="px-3 pb-1 text-[11px] text-ed-red">{err}</div> : null}
      {link ? (
        <div className="px-3 pb-1"><CompactText value={link} onChange={() => {}} /></div>
      ) : null}
      <div className="px-3 pb-2 text-[11px] text-ed-text-3">
        {blocked
          ? `${blocked}.`
          : "Each click mints a hosted link carrying the scene as it looks now. The viewer link is public; the editor link needs workspace access."}
      </div>
    </>
  );
}

// ─── Material extension master toggles ─────────────────────────────────────

export function MaterialExtensionTogglesControl() {
  // Master availability gates (all ship ON in iJewel). They are NOT wired to
  // anything: the real per-material extension editor (MaterialExtensionsControl)
  // reads the viewer's own extension config over RPC and never consults this
  // component. They used to be live checkboxes backed by a local useState, so
  // unchecking one looked like it disabled an extension, changed nothing, and
  // silently snapped back to ON when the section was collapsed (which unmounts
  // its rows). Rendered read-only until there is a real master-gate RPC.
  // The six "Always on" rows are gone: they said nothing the sentence below does
  // not, and six rows of unchangeable text read as controls. The SECTION stays
  // (the parity guard asserts its verbatim title) and now just points at the
  // editor that does the work.
  return (
    <div className="px-3 py-2 text-[11px] text-ed-text-3">
      Every material extension ships enabled in this build. Turn individual
      effects on or off per material in Material → Material Extensions.
    </div>
  );
}

// ─── Extras (advanced/debug) (ExtrasUiPlugin) ──────────────────────────────
/**
 * Session keys that must survive "Clear local storage". The button ran a bare
 * `localStorage.clear()` in the PLAYGROUND origin, which wiped the Vault auth
 * token and active workspace — signing the user out with no warning while React
 * still believed it was authenticated, so the next API call 401'd.
 */
const PROTECTED_STORAGE_KEYS = ["auth_token", "active_workspace_id", "invite_token"];

/** The four schema-parity placeholders below have no RPC at all — they are
 *  webgi renderer internals our in-house frame pipeline never exposed. */

export function ExtrasControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

  const clearStorage = async () => {
    setErr(null);
    setNote(null);
    const ok = await confirmDialog({
      title: "Clear local storage?",
      body: "Clears this site's cached editor preferences and recent designs. Your sign-in and active workspace are kept.",
      confirmLabel: "Clear",
      danger: true,
    });
    if (!ok) return;
    try {
      const doomed = Object.keys(localStorage).filter((k) => !PROTECTED_STORAGE_KEYS.includes(k));
      doomed.forEach((k) => localStorage.removeItem(k));
      setNote(`${doomed.length} key(s) cleared. Sign-in kept.`);
    } catch (e) {
      setErr(`Clear local storage failed: ${msg(e)}`);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 px-3 py-1.5">
      <div className="flex flex-wrap gap-1.5">
      </div>
      <div className="flex flex-wrap gap-1.5">
        <MiniBtn icon="delete_sweep" onClick={() => void clearStorage()}>Clear local storage</MiniBtn>
        <MiniBtn
          icon="cached"
          busy={busy}
          onClick={() => {
            setErr(null); setNote(null); setBusy(true);
            void r.clearCaches()
              .then(() => setNote("Viewer caches cleared."))
              .catch((e) => setErr(`Clear caches failed: ${msg(e)}`))
              .finally(() => setBusy(false));
          }}
        >
          Clear caches
        </MiniBtn>
        <MiniBtn
          icon="filter_center_focus"
          primary
          onClick={() => {
            setErr(null); setNote(null);
            void r.autoCenterAllGeometries()
              .then(() => setNote("Geometries re-centred."))
              .catch((e) => setErr(`Auto-center failed: ${msg(e)}`));
          }}
        >
          Auto Center All Geometries
        </MiniBtn>
      </div>
      {err ? <div className="text-[11px] text-ed-red">{err}</div> : null}
      {note ? <div className="text-[11px] text-ed-text-3">{note}</div> : null}
      <div className="text-[11px] text-ed-text-3">
        RGBM / MSAA / depth-prepass / debug and GPU instancing are engine internals
        our in-house pipeline does not expose — shown disabled for schema parity.
        Clear storage / caches & auto-center are live.
      </div>
    </div>
  );
}
